import {
  ITaskDenormalized,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@material-ui/core';
import { capitalize, identity } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  UnsafeTFunction,
  useUnsafeTranslation
} from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../layout/Flex';
import { LightMdToJsx } from '../../../../../../services/formatter';
import {
  humanizeDuration,
  ONE_DAY,
  toMoment
} from '../../../../../../services/time';
import { useProductAnalytics } from '../../../../../features/Experience/services/productAnalytics';
import { getIndividualTaskUrl } from '../../services/routes';
import { WithBreakpoints } from '../Breakpoints';
import { EmployeeAppCard } from '../EmployeeAppCard';
import { TaskStatusChipV2 } from '../TaskStatusChipV2';

const getDateString = (
  due: moment.Moment,
  mom: moment.Moment,
  t: UnsafeTFunction,
  opts: {
    capitalize?: boolean;
  } = {}
) => {
  const diff = due.diff(mom, 'days');
  const cap: (s: string) => string = opts.capitalize ? capitalize : identity;
  if (diff < -14 || diff > 14) {
    return due.format('MMM Do');
  }
  if (diff === 0) {
    if (due.isSame(mom, 'day')) {
      return cap(t('today'));
    } else {
      return cap(due.isAfter(mom) ? t('tomorrow') : t('yesterday'));
    }
  }

  return humanizeDuration(diff * ONE_DAY, true);
};

const RedText = styled('span')((p) => ({
  color: p.theme.custom.colors.red.w600
}));

export const DueDate = ({
  ts,
  highlightAsOverdue
}: {
  ts: Timestamp;
  highlightAsOverdue?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const dueString = useMemo(() => getDateString(toMoment(ts), moment(), t), [
    ts,
    t
  ]);
  const body = (
    <LightMdToJsx>{t('Due **{{ date }}**', { date: dueString })}</LightMdToJsx>
  );
  return (
    <Tooltip title={toMoment(ts).format('MMMM Do, YYYY HH:mm')}>
      <Typography variant="caption">
        {highlightAsOverdue ? <RedText>{body}</RedText> : body}
      </Typography>
    </Tooltip>
  );
};

export const CompletedDate = ({
  ts,
  short
}: {
  ts: Timestamp | null;
  short?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const dueString = useMemo(
    () => (ts ? getDateString(toMoment(ts), moment(), t) : ''),
    [ts, t]
  );
  if (!ts) {
    return null;
  }
  return (
    <Tooltip title={toMoment(ts).format('MMMM Do, YYYY HH:mm')}>
      <Typography variant="caption">
        <LightMdToJsx>
          {short
            ? `**${dueString}**`
            : t('Completed **{{ date }}**', { date: dueString })}
        </LightMdToJsx>
      </Typography>
    </Tooltip>
  );
};

export const DueOrCompletionDate = <
  X extends {
    status: TaskStatus;
    completedAt: Timestamp | null;
    end: Timestamp;
  }
>({
  task,
  short
}: {
  task: X;
  short?: boolean;
}) => {
  if (task.status === TaskStatus.COMPLETE && task.completedAt) {
    return <CompletedDate ts={task.completedAt} short={short} />;
  }
  return <DueDate ts={task.end} />;
};

export const PlainDate = ({ ts }: { ts: Timestamp | null }) => {
  const { t } = useUnsafeTranslation();
  const dString = useMemo(
    () =>
      ts ? getDateString(toMoment(ts), moment(), t, { capitalize: true }) : '',
    [ts, t]
  );
  if (!ts) {
    return null;
  }
  return (
    <Tooltip title={toMoment(ts).format('MMMM Do, YYYY HH:mm')}>
      <Typography variant="caption">
        <LightMdToJsx>{t('**{{ date }}**', { date: dString })}</LightMdToJsx>
      </Typography>
    </Tooltip>
  );
};

export const TaskWithDueDate = ({ d }: { d: ITaskDenormalized }) => {
  return (
    <FlexContainer
      fullWidth
      justifyContent="space-between"
      paddingTop={0.5}
      paddingBottom={0.5}
    >
      <Typography variant="h6">{d.title}</Typography>
      <DueDate ts={d.end} />
    </FlexContainer>
  );
};

export const TaskLink: React.FC<{
  actorAccessToken: string;
  instanceAccessToken: string;
  taskId: string;
  trackTaskCardEvent?: boolean;
  returnTo?: string;
}> = ({
  actorAccessToken,
  instanceAccessToken,
  taskId,
  children,
  trackTaskCardEvent = true,
  returnTo
}) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  return (
    <Link
      to={getIndividualTaskUrl(
        actorAccessToken,
        instanceAccessToken,
        taskId,
        returnTo
      )}
      onClick={
        trackTaskCardEvent
          ? () =>
              trackEvent({
                eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
                name: 'Task Card Clicked',
                Explanation: 'User clicked on a task card to open that task',
                'Task ID': taskId
              })
          : undefined
      }
      style={{ width: '100%' }}
    >
      {children}
    </Link>
  );
};

export const TaskWithStatus = ({ d }: { d: ITaskDenormalized }) => {
  return (
    <FlexContainer fullWidth spacing={2} paddingTop={0.5} paddingBottom={0.5}>
      <TaskStatusChipV2 status={d.status} />
      <Typography variant="h6">{d.title}</Typography>
    </FlexContainer>
  );
};

export const TaskWithStatusAndCompletionOrDueDate = ({
  d
}: {
  d: ITaskDenormalized;
}) => {
  return (
    <WithBreakpoints
      lgMdSm={
        <FlexContainer
          fullWidth
          spacing={2}
          paddingTop={0.5}
          paddingBottom={0.5}
        >
          <TaskStatusChipV2 status={d.status} />
          <FlexItem flex={1}>
            <Typography variant="h6">{d.title}</Typography>
          </FlexItem>
          {d.status === TaskStatus.COMPLETE ? (
            <CompletedDate ts={d.completedAt} />
          ) : (
            <DueDate ts={d.end} />
          )}
        </FlexContainer>
      }
      xs={
        <FlexContainerVertical spacing={1} fullWidth>
          <Typography variant="h6">{d.title}</Typography>
          <FlexContainer justifyContent="space-between" spacing={2} fullWidth>
            {d.status === TaskStatus.COMPLETE ? (
              <CompletedDate ts={d.completedAt} />
            ) : (
              <DueDate ts={d.end} />
            )}
            <TaskStatusChipV2 status={d.status} />
          </FlexContainer>
        </FlexContainerVertical>
      }
    />
  );
};

export const TaskCard = ({
  d,
  actorAccessToken,
  instanceAccessToken,
  returnTo
}: {
  d: ITaskDenormalized;

  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
}) => {
  return (
    <EmployeeAppCard hoverEffect>
      <TaskLink
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
        taskId={d.id}
        returnTo={returnTo}
      >
        <FlexContainerVertical fullWidth spacing={1}>
          <TaskWithStatusAndCompletionOrDueDate d={d} />
        </FlexContainerVertical>
      </TaskLink>
    </EmployeeAppCard>
  );
};
